import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
} from '@angular/core';
import type { UntypedFormArray } from '@angular/forms';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

import { noWhitespaceValidator } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import {
  BoxComponent,
  CheckboxComponent,
  DialogService,
  DialogWidth,
  DividerComponent,
  HeadingComponent,
  IconComponent,
  IconHelpComponent,
  LabelComponent,
  OfferStatusIndicatorComponent,
  RtTooltip,
} from '@recruitee/user-interface';
import { getDifferences, groupByKey } from '@recruitee/utils';

import { COUNTRY_CODES, REGIONS } from '../constants';
import { GfeaAddressInputComponent } from '../gfea-address-input/gfea-address-input.component';
import type { Offer, PositionLocationGroup, Posting } from '../types';

@Component({
  selector: 'rti-gfea-duplicate-jobs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gfea-duplicate-jobs.component.html',
  styleUrls: ['./gfea-duplicate-jobs.component.less'],
  imports: [
    BoxComponent,
    CheckboxComponent,
    DividerComponent,
    GfeaAddressInputComponent,
    HeadingComponent,
    IconComponent,
    IconHelpComponent,
    LabelComponent,
    NgTemplateOutlet,
    OfferStatusIndicatorComponent,
    RtTooltip,
    TranslatePipe,
  ],
})
export class GfeaDuplicateJobsComponent {
  @Input() public duplicateOfferIdsControl: UntypedFormControl;
  @Input() public positionLocationsArray: UntypedFormArray;
  @Input() public posting?: Posting;
  @Input() public offer: Offer;
  @Input() public selectableOffers: Offer[];

  public offerMap: { [id: number]: Offer } = {};
  public ngOnDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private injector: Injector,
    private dialogService: DialogService,
  ) {}

  public ngOnInit(): void {
    this.offerMap[this.offer.id] = this.offer;
    this.selectableOffers.forEach(offer => {
      this.offerMap[offer.id] = offer;
    });
    this.initializePositionLocationsArray(this.duplicateOfferIdsControl.value);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public async openJobsDuplicationDialog(): Promise<void> {
    const { GfeaDuplicateOffersDialogComponent } = await import(
      '../gfea-duplicate-offers-dialog/gfea-duplicate-offers-dialog.component'
    );

    this.dialogService
      .openCustom(
        GfeaDuplicateOffersDialogComponent,
        { width: DialogWidth.Small, injector: this.injector },
        {
          duplicateOfferIds: this.duplicateOfferIdsControl.value || [],
          offers: this.selectableOffers,
        },
      )
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(selectedOfferIds => {
        this.setDuplicateOfferIdsControl(selectedOfferIds);
        this.initializePositionLocationsArray(selectedOfferIds);
      });
  }

  public trackByPositionLocationGroup(_index: number, group: UntypedFormGroup): any {
    return group;
  }

  public trackByOfferId(_index: number, offerId: number): number {
    return offerId;
  }

  private setDuplicateOfferIdsControl(selectedOfferIds: number[]): void {
    if (!selectedOfferIds) return;

    this.duplicateOfferIdsControl.markAsDirty();

    if (selectedOfferIds.length === 0) {
      this.duplicateOfferIdsControl.setValue(null);
    } else {
      this.duplicateOfferIdsControl.setValue(selectedOfferIds);
    }

    this.cdRef.detectChanges();
  }

  private initializePositionLocationsArray(selectedOfferIds: number[] | null): void {
    if (!selectedOfferIds) return;

    if (selectedOfferIds.length === 0) {
      this.positionLocationsArray.disable();
      this.positionLocationsArray.clear();
      this.positionLocationsArray.setValue([]);
    } else if (selectedOfferIds.length < 10) {
      this.initializeFullLocationsForm(selectedOfferIds);
    } else {
      this.initializeGroupedLocationsForm(selectedOfferIds);
    }

    this.cdRef.detectChanges();
  }

  private initializeFullLocationsForm(selectedOfferIds: number[]): void {
    const intialPositionLocations = this.posting?.data.offerData.positionLocations;

    this.positionLocationsArray.clear({ emitEvent: false });
    const offers = selectedOfferIds
      .map(offerId => this.selectableOffers.find(offer => offer.id === offerId))
      .filter(offer => !!offer);
    offers.unshift(this.offer);
    offers.forEach(offer => {
      const formGroup = this.fullLocationFormGroup();
      this.prefillFullLocationFormGroup(formGroup, intialPositionLocations, offer!);
      this.positionLocationsArray.push(formGroup, { emitEvent: false });
    });

    this.positionLocationsArray.enable();
  }

  private fullLocationFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      offerIds: new UntypedFormControl([]),
      location: new UntypedFormGroup({
        countryCode: new UntypedFormControl('', [Validators.required]),
        postalCode: new UntypedFormControl('', [
          Validators.required,
          Validators.maxLength(15),
          noWhitespaceValidator,
        ]),
        region: new UntypedFormControl('', [Validators.required]),
        municipality: new UntypedFormControl('', [
          Validators.required,
          Validators.maxLength(36),
          noWhitespaceValidator,
        ]),
        streetName: new UntypedFormControl('', [Validators.maxLength(30), noWhitespaceValidator]),
      }),
    });
  }

  private prefillFullLocationFormGroup(
    group: UntypedFormGroup,
    intialPositionLocations: PositionLocationGroup[] | undefined,
    offer: Offer,
  ): void {
    const existentValue = intialPositionLocations?.find(
      (positionLocationGroup: PositionLocationGroup) =>
        positionLocationGroup.offerIds.length === 1 &&
        positionLocationGroup.offerIds[0] === offer.id,
    );

    const prefillValue = existentValue || {
      offerIds: [offer.id],
      location: {
        countryCode: COUNTRY_CODES.includes(offer.countryCode!) ? offer.countryCode : '',
        region: REGIONS.find(region => region.name === offer.stateName)?.vamCode || '',
        postalCode: offer.postalCode || '',
        municipality: offer.city || '',
        streetName: offer.street || '',
      },
    };

    group.patchValue(prefillValue, { emitEvent: false });
  }

  private initializeGroupedLocationsForm(selectedOfferIds: number[]): void {
    const intialPositionLocations = this.posting?.data.offerData.positionLocations;

    this.positionLocationsArray.clear({ emitEvent: false });
    const offers = selectedOfferIds
      .map(offerId => this.selectableOffers.find(offer => offer.id === offerId)!)
      .filter(offer => !!offer);
    offers.unshift(this.offer);

    const groups = groupByKey(
      offers.map(offer => ({ key: `${offer.countryCode}-${offer.stateCode}`, offer })),
      'key',
    );
    const groupKeys = Object.keys(groups);

    if (groupKeys.length > 10) {
      // More than 10 groups - group by country code instead.
      // More than 10 different countries just get ignored with `.slice`.
      const groups = groupByKey(offers, `countryCode`);
      const groupKeys = Object.keys(groups)
        .filter(groupKey => !!groupKey)
        .slice(0, 10);

      groupKeys.forEach(groupKey => {
        const offers = groups[groupKey];

        const formGroup = this.countryLocationFormGroup();
        this.prefillGroupedLocationFormGroup(formGroup, intialPositionLocations, offers);
        this.positionLocationsArray.push(formGroup, { emitEvent: false });
      });
    } else {
      // Less than 10 groups - we're okay to group by both country and state

      groupKeys.forEach(groupKey => {
        const offers = groups[groupKey].map(groupElement => groupElement.offer);

        const formGroup = this.countryAndRegionLocationFormGroup();
        this.prefillGroupedLocationFormGroup(formGroup, intialPositionLocations, offers);
        this.positionLocationsArray.push(formGroup, { emitEvent: false });
      });
    }

    this.positionLocationsArray.enable();
  }

  private countryLocationFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      offerIds: new UntypedFormControl([]),
      location: new UntypedFormGroup({
        countryCode: new UntypedFormControl('', [Validators.required]),
      }),
    });
  }

  private countryAndRegionLocationFormGroup(): UntypedFormGroup {
    return new UntypedFormGroup({
      offerIds: new UntypedFormControl([]),
      location: new UntypedFormGroup({
        countryCode: new UntypedFormControl('', [Validators.required]),
        region: new UntypedFormControl('', [Validators.required]),
      }),
    });
  }

  private prefillGroupedLocationFormGroup(
    group: UntypedFormGroup,
    intialPositionLocations: PositionLocationGroup[] | undefined,
    offers: Offer[],
  ): void {
    const offerIds = offers.map(offer => offer.id);

    const existentValue = intialPositionLocations?.find(
      (positionLocationGroup: PositionLocationGroup) =>
        positionLocationGroup.offerIds.length === offerIds.length &&
        !getDifferences(positionLocationGroup.offerIds, offerIds).length,
    );

    const prefillValue = existentValue || {
      offerIds,
      location: {
        countryCode: COUNTRY_CODES.includes(offers[0].countryCode!) ? offers[0].countryCode : '',
        region: REGIONS.find(region => region.name === offers[0].stateName)?.vamCode || '',
      },
    };

    group.patchValue(prefillValue, { emitEvent: false });
  }
}
